import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Fields from "./fields"
import { defaultFormTheme } from "../../../../atoms/defaultFormTheme";
import { useSnackbar } from "notistack";
import { RequestServer } from "../../../../utils/services";
import { APIRoutes } from "../../../../constants";
import { useFullPageLoading } from "../../../../atoms/full-page-loading/loadingContext";
import { removeWhiteSpacesFromForm } from "../../../../utils/utils";
import { useEffect, useState } from "react";

export type MetabaseEntry = {
    name: string;
    id: string;
    params: string;
    type: "question" | "dashboard";
    order?: number;
};

export type Props = {
    handleClose: () => void;
    isChange: any;
    handleSubmitClose: () => void;
    admin_id: string | null;
    dashboards: any;
    refetchAdmin: any;
}

const AdminDashboard = ({ handleClose, isChange, handleSubmitClose, admin_id ,dashboards,refetchAdmin }: Props) => {
    const { enqueueSnackbar } = useSnackbar();
    const { setLoading } = useFullPageLoading();




    const onSubmit: SubmitHandler<{ metabaseEntries: MetabaseEntry[] }> =
        async (
            formData: {
                metabaseEntries: MetabaseEntry[]
            }
        ) => {
            const entries = formData.metabaseEntries.map((entry, index) => ({
                ...entry,
                order: index,
            }));
            // Convert array of entries to metabase object format
            const metabaseObj: { [key: string]: { id: string; order: number; params: any; type: string } } = {};
            if (entries && entries.length > 0) {
                for (const entry of entries) {
                    if (entry.name && entry.id && entry.type) {
                        let parsedParams = {};

                        // Parse JSON params if provided
                        if (entry.params && entry.params.trim()) {
                            try {
                                parsedParams = JSON.parse(entry.params);
                            } catch (error) {
                                enqueueSnackbar(`Invalid JSON in params for "${entry.name}": ${error}`, {
                                    variant: "error"
                                });
                                return;
                            }
                        }

                        metabaseObj[entry.name] = {
                            id: entry.id,
                            order: entry.order,
                            params: parsedParams,
                            type: entry.type
                        };
                    }
                }
            }

            if (!admin_id) {
                return;
            }

            const payload = {
                id: parseInt(admin_id),
                metabase: Object.keys(metabaseObj).length > 0 ? metabaseObj : {},
            };

            try {
                const response = await RequestServer(
                    APIRoutes.UPDATE_METABASE_URL,
                    "PATCH",
                    payload
                );

                if (response.success) {
                    enqueueSnackbar("Employee edited.", {
                        variant: "success",
                    });

                } else {
                    enqueueSnackbar(response.message.toString(), { variant: "warning" });
                }
            } catch (err) {
                console.error({ err });
                enqueueSnackbar("Request failed.", { variant: "error" });
            } finally {
                setLoading(false);
                refetchAdmin();
                handleSubmitClose();
            }
        }

    // Convert existing dashboards to array format for form
    const convertDashboardsToEntries = (dashboards: any): MetabaseEntry[] => {
        if (!dashboards) return [{ name: "", id: "", params: "", type: "question" }];

        if (typeof dashboards === "object") {
            const entries = Object.entries(dashboards).map(([name, value]: [string, any]) => {
                let paramsString = "";

                // Handle params serialization
                if (value.params) {
                    if (typeof value.params === "object") {
                        try {
                            paramsString = JSON.stringify(value.params, null, 2);
                        } catch (error) {
                            console.warn("Failed to stringify params for", name, error);
                            paramsString = "";
                        }
                    } else if (typeof value.params === "string") {
                        paramsString = value.params;
                    }
                }

                return {
                    name,
                    id: value.id,
                    params: paramsString,
                    type: value.type || "dashboard",
                    order: value.order || 0
                };
            });
            entries.sort((a, b) => a.order - b.order);

            return entries.length > 0 ? entries : [{ name: "", id: "", params: "", type: "question" }];
        }

        // If it's still in old string format, return default entry
        return [{ name: "", id: "", params: "", type: "question" }];
    };

    const defaultValues = {
        metabaseEntries: convertDashboardsToEntries(dashboards),
    }

    const methods = useForm({ defaultValues });

    const defaultTheme = createTheme(defaultFormTheme);

    return (
        <FormProvider {...methods}>
            <ThemeProvider theme={defaultTheme}>
                <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                    <Fields handleClose={handleClose} isChange={isChange} />
                </form>
            </ThemeProvider>
        </FormProvider>
    );
};

export default AdminDashboard;
